import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Wizard modal that combines AI-suggested metadata (title, category, tags,
// icon) with a live preview of the markdown content. Used in two flows:
//
// 1. Create-from-paste — the wizard auto-suggests metadata for fresh content
//    pasted from the clipboard, then creates the readme on Save.
// 2. Auto-fill on existing readme — the user clicks "✦ Auto-fill" in the
//    editor toolbar; the wizard seeds with current values, optionally
//    suggests fresh ones, and patches the readme via the existing autosave.
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { IconPicker } from "@/components/IconPicker";
import { MarkdownView } from "@/components/MarkdownView";
import { usePreferences } from "@/lib/preferences";
import { READMECATEGORIES, } from "@shared/types";
function tagsToString(tags) {
    return tags.join(", ");
}
function stringToTags(raw) {
    return raw
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t.length > 0)
        .slice(0, 20);
}
function tagsEqual(a, b) {
    if (a.length !== b.length)
        return false;
    for (let i = 0; i < a.length; i++)
        if (a[i] !== b[i])
            return false;
    return true;
}
export function MetadataWizardModal(props) {
    const { prefs } = usePreferences();
    const { initialContent, initialTitle, initialCategory, initialTags, initialIcon } = props;
    const [content, setContent] = useState(initialContent);
    const [title, setTitle] = useState(initialTitle ?? "");
    const [category, setCategory] = useState(initialCategory ?? null);
    const [tagsText, setTagsText] = useState(tagsToString(initialTags ?? []));
    const [icon, setIcon] = useState(initialIcon ?? null);
    const [showRawEdit, setShowRawEdit] = useState(false);
    const [suggesting, setSuggesting] = useState(false);
    const [suggestError, setSuggestError] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [saving, setSaving] = useState(false);
    // Track which fields the user has manually edited so a re-suggest doesn't
    // clobber them. Initial values seeded as untouched.
    const touchedRef = useRef(new Set());
    const markTouched = (f) => { touchedRef.current.add(f); };
    const knownCategories = useMemo(() => [...READMECATEGORIES, ...prefs.customCategories], [prefs.customCategories]);
    async function runSuggest(force = false) {
        if (suggesting)
            return;
        setSuggesting(true);
        setSuggestError(false);
        try {
            const cfg = prefs.aiConfig.readmes;
            const res = await api.suggestMeta({
                content,
                knownCategories,
                provider: cfg.provider === "auto" ? undefined : cfg.provider,
                model: cfg.model === "auto" ? undefined : cfg.model,
            });
            const touched = touchedRef.current;
            if (force || (!touched.has("title") && !title))
                setTitle(res.title);
            if (force || (!touched.has("category") && !category))
                setCategory(res.category);
            if (force || (!touched.has("tags") && stringToTags(tagsText).length === 0)) {
                setTagsText(tagsToString(res.tags));
            }
            if (force || (!touched.has("icon") && !icon))
                setIcon(res.icon);
        }
        catch {
            setSuggestError(true);
        }
        finally {
            setSuggesting(false);
        }
    }
    // Auto-suggest once on mount when seeded title is empty (paste-create flow).
    const didAutoSuggestRef = useRef(false);
    useEffect(() => {
        if (didAutoSuggestRef.current)
            return;
        didAutoSuggestRef.current = true;
        const hasSeedTitle = (initialTitle ?? "").trim().length > 0;
        if (!hasSeedTitle && content.trim().length > 0) {
            void runSuggest(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const tagsArray = useMemo(() => stringToTags(tagsText), [tagsText]);
    async function handleSave() {
        setSaveError(null);
        setSaving(true);
        try {
            const finalTitle = title.trim() || "Untitled";
            const finalCategory = category && knownCategories.includes(category)
                ? (knownCategories.find((c) => c === category) ?? null)
                : null;
            const isBuiltinCat = finalCategory && READMECATEGORIES.includes(finalCategory);
            const finalCategoryForApi = isBuiltinCat
                ? finalCategory
                : null;
            if (props.mode === "create") {
                await props.onCreate({
                    title: finalTitle,
                    content,
                    category: finalCategoryForApi,
                    tags: tagsArray,
                    icon: icon ?? null,
                });
            }
            else {
                const patch = {};
                if (finalTitle !== (initialTitle ?? ""))
                    patch.title = finalTitle;
                const initialCat = (initialCategory ?? null);
                if (finalCategoryForApi !== initialCat)
                    patch.category = finalCategoryForApi;
                if (!tagsEqual(tagsArray, initialTags ?? []))
                    patch.tags = tagsArray;
                if ((icon ?? null) !== (initialIcon ?? null))
                    patch.icon = icon ?? null;
                props.onPatch(patch);
            }
        }
        catch (err) {
            setSaveError(err instanceof Error ? err.message : "Failed to save.");
        }
        finally {
            setSaving(false);
        }
    }
    return (_jsx("div", { className: "fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 p-3 sm:items-center", onClick: props.onClose, children: _jsxs("div", { className: "card w-full max-w-3xl p-4 sm:p-6", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "mb-3 flex items-center justify-between gap-2", children: [_jsx("h2", { className: "text-lg font-semibold", children: props.mode === "create" ? "New readme" : "Auto-fill metadata" }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("button", { type: "button", className: "btn-secondary text-xs", onClick: () => runSuggest(true), disabled: suggesting, title: "Re-run AI suggestion for all empty fields", children: suggesting ? "Suggesting…" : "✦ Suggest" }), _jsx("button", { type: "button", "aria-label": "Close", className: "rounded p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800", onClick: props.onClose, children: "\u2715" })] })] }), suggestError && (_jsx("div", { className: "mb-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-200", children: "AI suggest failed \u2014 fields left as-is. You can edit manually." })), _jsxs("div", { className: "grid gap-4 sm:grid-cols-2", children: [_jsxs("div", { className: "space-y-3", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(IconPicker, { current: icon, onChange: (v) => { markTouched("icon"); setIcon(v); } }), _jsx("input", { className: "input flex-1 text-base font-medium", placeholder: "Title", value: title, onChange: (e) => { markTouched("title"); setTitle(e.target.value); } })] }), _jsxs("div", { children: [_jsx("label", { className: "mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500", children: "Category" }), _jsxs("select", { className: "input w-full", value: category ?? "", onChange: (e) => {
                                                markTouched("category");
                                                setCategory(e.target.value || null);
                                            }, children: [_jsx("option", { value: "", children: "\u2014 none \u2014" }), knownCategories.map((c) => (_jsx("option", { value: c, children: c }, c)))] })] }), _jsxs("div", { children: [_jsxs("label", { className: "mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500", children: ["Tags ", _jsx("span", { className: "font-normal normal-case text-slate-400", children: "(comma-separated)" })] }), _jsx("input", { className: "input w-full", placeholder: "tag1, tag2, tag3", value: tagsText, onChange: (e) => { markTouched("tags"); setTagsText(e.target.value); } }), tagsArray.length > 0 && (_jsx("div", { className: "mt-1.5 flex flex-wrap gap-1", children: tagsArray.map((t) => (_jsxs("span", { className: "rounded bg-sky-50 px-1.5 py-0.5 text-xs text-sky-700 dark:bg-sky-950/40 dark:text-sky-300", children: ["#", t] }, t))) }))] })] }), _jsxs("div", { children: [_jsxs("div", { className: "mb-1 flex items-center justify-between", children: [_jsx("label", { className: "text-xs font-medium uppercase tracking-wide text-slate-500", children: props.mode === "create" ? "Content" : "Preview" }), props.mode === "create" && (_jsx("button", { type: "button", className: "text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-200", onClick: () => setShowRawEdit((v) => !v), children: showRawEdit ? "Preview" : "Edit raw" }))] }), _jsx("div", { className: "max-h-[50vh] overflow-auto rounded-md border border-slate-200 p-2 dark:border-slate-700", children: props.mode === "create" && showRawEdit ? (_jsx("textarea", { className: "input min-h-[40vh] w-full font-mono text-xs", value: content, onChange: (e) => setContent(e.target.value) })) : (_jsx(MarkdownView, { source: content || "_(empty)_" })) })] })] }), saveError && (_jsx("div", { className: "mt-3 rounded-md bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-950/40 dark:text-rose-300", children: saveError })), _jsxs("div", { className: "mt-4 flex justify-end gap-2", children: [_jsx("button", { className: "btn-secondary", onClick: props.onClose, disabled: saving, children: "Cancel" }), _jsx("button", { className: "btn-primary", onClick: handleSave, disabled: saving || (props.mode === "create" && content.trim().length === 0), children: saving
                                ? "Saving…"
                                : props.mode === "create"
                                    ? "Create"
                                    : "Apply" })] })] }) }));
}
